import React, { useCallback, useMemo, useRef, useState } from "react";
import { ColorPicker } from "antd";
import { useRecentColors } from "../hooks/useRecentColors";
import { createColorPickerPanel } from "../utils/colorPickerPanel";
import { transformColor } from "../../core/utils/generalUtils";

/**
 * InlineQuickColorPicker
 * - Small, inline trigger (swatch) that opens the same AntD color palette UI as `ColorField`
 * - Includes "Special" palette and Recent Colors panel
 * - Controlled open state so we can stopPropagation safely (important inside the editor canvas)
 */
const InlineQuickColorPicker = ({
  value,
  onChange,
  triggerClassName = "",
  swatchClassName = "",
  disabled = false,
  title = "Change color",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const lastSentColorRef = useRef(null);
  const {
    recentColors,
    setPendingColor,
    addPendingColor,
    addColorImmediately,
    currentPageColors,
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage,
  } = useRecentColors();

  const normalizedValue = useMemo(() => {
    if (!value) return "#ffffff";
    if (typeof value === "string" && value.startsWith("#")) return value;
    try {
      return transformColor(value, 100, "hex");
    } catch (_) {
      return "#ffffff";
    }
  }, [value]);

  const handleColorChange = useCallback(
    (color) => {
      const hexColor = color?.toHexString?.() || null;
      if (!hexColor) return;
      if (lastSentColorRef.current === hexColor) return;
      lastSentColorRef.current = hexColor;
      onChange?.(hexColor);
      setPendingColor(hexColor);
    },
    [onChange, setPendingColor],
  );

  const handleColorSelect = useCallback(
    (hex) => {
      if (!hex) return;
      if (lastSentColorRef.current === hex) {
        setIsOpen(false);
        return;
      }
      lastSentColorRef.current = hex;
      onChange?.(hex);
      setPendingColor(hex);
      setIsOpen(false);
    },
    [onChange, setPendingColor],
  );

  // Keep `panelRender` identity stable (same trick as ColorField)
  const latestRef = useRef(null);
  latestRef.current = {
    normalizedValue,
    handleColorSelect,
    recentColors,
    currentPageColors,
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage,
    addColorImmediately,
  };
  const panelRender = useCallback((panel) => {
    const latest = latestRef.current;
    if (!latest) return panel;
    return createColorPickerPanel({
      currentValue: latest.normalizedValue,
      onColorSelect: latest.handleColorSelect,
      recentColors: latest.recentColors,
      currentPageColors: latest.currentPageColors,
      currentPage: latest.currentPage,
      totalPages: latest.totalPages,
      goToNextPage: latest.goToNextPage,
      goToPrevPage: latest.goToPrevPage,
      addColorImmediately: latest.addColorImmediately,
    })(panel);
  }, []);

  return (
    <ColorPicker
      value={normalizedValue}
      onChange={handleColorChange}
      panelRender={panelRender}
      open={isOpen}
      disabled={disabled}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) addPendingColor();
      }}
    >
      <button
        type="button"
        title={title}
        aria-label={title}
        disabled={disabled}
        className={triggerClassName}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          setIsOpen(true);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span
          className={swatchClassName}
          style={{ backgroundColor: normalizedValue }}
          aria-hidden="true"
        />
      </button>
    </ColorPicker>
  );
};

export default InlineQuickColorPicker;

